import type { FileMetadata } from './types';

export interface FieldOption {
  value: string;
  label: string;
}

export interface FieldSchema {
  key: keyof FileMetadata;
  label: string;
  type: 'select' | 'date' | 'textarea' | 'boolean' | 'wildfire-autocomplete';
  required: boolean;
  copyToAll: boolean;
  placeholder?: string;
  filterByExtension?: boolean;
  // Inline options for selects that don't come from a taxonomy vocabulary.
  options?: FieldOption[];
  // When true, the field renders inside a collapsed "More details" group per card.
  collapsed?: boolean;
  // For wildfire-autocomplete: restrict matches to current-year wildfire terms.
  currentYearOnly?: boolean;
  // Conditional visibility — when set, the field is only rendered (and only
  // required, if `required: true`) when the file's selected subcategory term
  // name matches. Used for the Perimeter Source field on fire_file uploads.
  visibleWhenSubcategoryName?: string;
  // Keys (matching the field's value) that should trigger a "please describe
  // in the Description field" hint below the input.
  needsDescriptionWhenValueIn?: readonly string[];
}

export interface ContentTypeUploadSchema {
  subcategoryVocabulary?: string;
  fields: FieldSchema[];
  title: (identifier?: string) => string;
}

const COMPASS_16: FieldOption[] = [
  'N', 'NNE', 'NE', 'ENE', 'E', 'ESE', 'SE', 'SSE',
  'S', 'SSW', 'SW', 'WSW', 'W', 'WNW', 'NW', 'NNW',
].map((d) => ({ value: d, label: d }));

import {
  PERIMETER_SOURCE_OPTIONS,
  PERIMETER_SOURCE_NEEDS_DESCRIPTION,
  PERIMETER_SUBCATEGORY_NAME,
} from '../../constants/perimeterSource';

const PERIMETER_SOURCE_FIELD_OPTIONS: FieldOption[] = PERIMETER_SOURCE_OPTIONS.map((o) => ({ value: o.value, label: o.label }));
const PERIMETER_SOURCE_NEEDS_DESCRIPTION_KEYS: readonly string[] = Array.from(PERIMETER_SOURCE_NEEDS_DESCRIPTION);

export const UPLOAD_SCHEMAS: Record<string, ContentTypeUploadSchema> = {
  fire_file: {
    subcategoryVocabulary: 'fire_file_subcategory',
    fields: [
      { key: 'subcategoryId', label: 'Subcategory', type: 'select', required: true, copyToAll: true, filterByExtension: true },
      {
        key: 'perimeterSource',
        label: 'Perimeter Source',
        type: 'select',
        required: true,
        copyToAll: true,
        options: PERIMETER_SOURCE_FIELD_OPTIONS,
        visibleWhenSubcategoryName: PERIMETER_SUBCATEGORY_NAME,
        needsDescriptionWhenValueIn: PERIMETER_SOURCE_NEEDS_DESCRIPTION_KEYS,
      },
      { key: 'fileDate', label: 'Date', type: 'date', required: true, copyToAll: true },
      { key: 'description', label: 'Description', type: 'textarea', required: true, copyToAll: false, placeholder: 'Describe this file' },
    ],
    title: (fireNumber) => fireNumber ? `Upload ${fireNumber} Files` : 'Upload Fire Files',
  },

  weather_file: {
    subcategoryVocabulary: 'weather_subcategory',
    fields: [
      { key: 'wildfireId', label: 'Fire ID', type: 'wildfire-autocomplete', required: false, copyToAll: true, currentYearOnly: true, placeholder: 'Optional — type to search current-year fires' },
      { key: 'subcategoryId', label: 'Subcategory', type: 'select', required: true, copyToAll: true, filterByExtension: false },
      { key: 'fileDate', label: 'Date', type: 'date', required: true, copyToAll: true },
      { key: 'description', label: 'Description', type: 'textarea', required: false, copyToAll: false, placeholder: 'Describe this file' },
    ],
    title: (fireNumber) => fireNumber ? `Upload ${fireNumber} Weather Files` : 'Upload Weather Files',
  },

  var_photo: {
    fields: [
      { key: 'description', label: 'Description', type: 'textarea', required: false, copyToAll: false, placeholder: 'Describe this photo' },
      { key: 'cardinal', label: 'Cardinal', type: 'select', required: false, copyToAll: false, options: COMPASS_16, collapsed: true },
      { key: 'disrepair', label: 'Disrepair', type: 'boolean', required: false, copyToAll: false, collapsed: true },
      { key: 'forestFuels', label: 'Forest Fuels', type: 'boolean', required: false, copyToAll: false, collapsed: true },
      { key: 'fuelMitigation', label: 'Fuel Mitigation', type: 'boolean', required: false, copyToAll: false, collapsed: true },
      { key: 'fuelProximity', label: 'Fuel Proximity', type: 'boolean', required: false, copyToAll: false, collapsed: true },
      { key: 'hazmat', label: 'Hazmat', type: 'boolean', required: false, copyToAll: false, collapsed: true },
    ],
    title: (varId) => varId ? `Upload VAR ${varId} Photos` : 'Upload VAR Photos',
  },
};
